import React, { useState } from 'react'
import { sendSingleFileEmail, sendAllFilesEmail, EmailSingleFileRequest, EmailAllFilesRequest } from '../api/emailApi'
import { logger } from '../utils/logger'

interface EmailModalProps {
  isOpen: boolean
  onClose: () => void
  orderId: string
  filename?: string // Если указан - отправка одного файла, иначе - всех файлов
  filesCount?: number // Количество файлов для отправки всех
}

export const EmailModal: React.FC<EmailModalProps> = ({
  isOpen,
  onClose,
  orderId,
  filename,
  filesCount = 0
}) => {
  const [email, setEmail] = useState('')
  const [comment, setComment] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const isSingleFile = !!filename
  const modalTitle = isSingleFile 
    ? 'Отправить файл по email' 
    : `Отправить все файлы по email`

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!email.trim()) {
      setError('Введите email получателя')
      return
    }

    // Простая валидация email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setError('Введите корректный email адрес')
      return
    }

    setIsLoading(true)
    setProgress(0)
    setError('')
    setSuccess('')

    // Симулируем прогресс отправки
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) return prev // Останавливаемся на 90%, дожидаемся ответа сервера
        return prev + Math.random() * 10 + 3 // Увеличиваем на 3-13%
      })
    }, 150)

    try {
      let result
      
      if (isSingleFile && filename) {
        const data: EmailSingleFileRequest = {
          recipient_email: email.trim(),
          filename,
          comment: comment.trim() || undefined
        }
        result = await sendSingleFileEmail(orderId, data)
      } else {
        const data: EmailAllFilesRequest = {
          recipient_email: email.trim(),
          comment: comment.trim() || undefined
        }
        result = await sendAllFilesEmail(orderId, data)
      }

      // Завершаем прогресс
      clearInterval(progressInterval)
      setProgress(100)

      if (result.success) {
        const attachedCount = result.attached_files?.length || 0
        const failedCount = result.failed_files?.length || 0
        
        let successMessage = `✅ Успешно отправлено файлов: ${attachedCount}`
        if (failedCount > 0) {
          successMessage += `, не удалось отправить: ${failedCount}`
        }
        
        setSuccess(successMessage)
        
        // Закрыть модальное окно через 2 секунды
        setTimeout(() => {
          onClose()
          resetForm()
        }, 2000)
      } else {
        setError(result.message || 'Ошибка отправки email')
      }
    } catch (err: any) {
      // Останавливаем прогресс при ошибке
      clearInterval(progressInterval)
      setProgress(0)
      
      logger.error('Email sending error:', err)
      logger.error('Error details:', {
        status: err.response?.status,
        data: err.response?.data,
        message: err.message
      })
      
      const errorMessage = err.response?.data?.detail?.message || 
                          err.response?.data?.message || 
                          err.message || 
                          'Ошибка отправки email'
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const resetForm = () => {
    setEmail('')
    setComment('')
    setError('')
    setSuccess('')
    setIsLoading(false)
    setProgress(0)
  }

  const handleClose = () => {
    if (!isLoading) {
      onClose()
      resetForm()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-lg p-4">
      <div className="bg-white p-6 rounded-2xl shadow-2xl w-full max-w-sm animate-fade-in-up">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-gray-200 mb-4">
          <div className="flex items-center space-x-2">
            <svg className="w-6 h-6 text-gray-600" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zM4 6h16l-8 5-8-5zm0 12V8l8 5 8-5v10H4z"/>
            </svg>
            <h2 className="text-lg font-bold text-gray-800">{modalTitle}</h2>
          </div>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-300 disabled:opacity-50"
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        {/* File info */}
        {isSingleFile && filename ? (
          <div className="mt-4 bg-green-50 text-gray-800 p-4 rounded-xl border border-green-200 shadow-sm text-sm">
            <p className="font-semibold text-green-800">Файл для отправки:</p>
            <div className="flex items-center mt-1">
              <svg className="w-5 h-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zM6 4h7v5h5v11H6V4z"/>
              </svg>
              <span className="text-green-900 font-medium truncate">{filename}</span>
            </div>
          </div>
        ) : filesCount ? (
          <div className="mt-4 bg-blue-50 text-gray-800 p-4 rounded-xl border border-blue-200 shadow-sm text-sm">
            <p className="font-semibold text-blue-800">Файлы для отправки:</p>
            <div className="flex items-center mt-1">
              <svg className="w-5 h-5 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 2a1 1 0 000 2h6a1 1 0 100-2H9z"/>
                <path fillRule="evenodd" d="M4 5a2 2 0 012-2v1a1 1 0 001 1h8a1 1 0 001-1V3a2 2 0 012 2v6.101A7.002 7.002 0 0010 15H7a1 1 0 100 2h3.101A7.002 7.002 0 0018 11.101V5z" clipRule="evenodd"/>
              </svg>
              <span className="text-blue-900 font-medium">Все файлы заказа ({filesCount} шт.)</span>
            </div>
          </div>
        ) : null}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email input */}
          <div className="mt-4">
            <label htmlFor="email" className="block text-gray-700 font-semibold mb-1 text-sm">
              Email получателя <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              placeholder="example@company.com"
              className="w-full p-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-200 focus:border-blue-500 transition-all duration-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              required
            />
          </div>

          {/* Comment input */}
          <div className="mt-3">
            <label htmlFor="comment" className="block text-gray-700 font-semibold mb-1 text-sm">
              Комментарий (необязательно)
            </label>
            <textarea
              id="comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              disabled={isLoading}
              rows={3}
              placeholder="Дополнительная информация..."
              className="w-full p-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-200 focus:border-blue-500 transition-all duration-300 resize-none text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {/* Success message */}
          {success && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-sm">
              <p className="text-green-700">{success}</p>
            </div>
          )}

          {/* Progress bar */}
          {isLoading && (
            <div className="mt-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-blue-700">Отправка файлов...</span>
                <span className="text-sm font-medium text-blue-700">{Math.round(progress)}%</span>
              </div>
              <div className="w-full bg-blue-100 rounded-full h-2">
                <div 
                  className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading}
              className="px-6 py-2 text-gray-700 font-bold bg-gray-200 rounded-xl hover:bg-gray-300 transition-all duration-300 transform hover:scale-105 text-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={isLoading || !email.trim()}
              className="px-6 py-2 text-white font-bold bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl shadow-md hover:from-blue-600 hover:to-blue-700 transition-all duration-300 transform hover:scale-105 text-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center min-w-[100px]"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {Math.round(progress)}%
                </>
              ) : (
                'Отправить'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
